/**
 * 63.12 (DECISIONS 243, 244; ARCHITECTURE §5.24), with GameConfig.vehicleZones: where on a vehicle a round or a burst struck,
 * and what of it reaches the people inside. `zoneOf` is the one door to a hull's zones (the owner, 244 (1): from the hit point
 * now; boxes fitted from the models' named parts may later back the same names without touching the rules here). Pure.
 */
import type { GameState, StructureState, Vec3 } from '../types';
import { VEHICLE_ZONES, ZONE_RULES, type VehicleClass, type VehicleZone, type ZoneTable, type ZonedVehicle } from '../constants/vehicleZones';
import { MOUNT_LAUNCHERS } from '../constants/action';
import { HITBOXES } from '../constants/hitboxes';
import type { BodyKey } from '../constants/body';
import { vehicleFrame } from './vehicleFrame';
import { personSetOf, PERSON_BODY_HEIGHT } from './scaleProfile';
import { vecDot, vecSub } from '../math';
import { dcos, dsin } from './dmath';
import { simWork } from './workCounters';

export type Local = readonly [number, number, number];

/**
 * Whether the zones decide hits on vehicles (off: the one health a vehicle, nobody inside reached while it stands). 69.39
 * (DECISIONS 253 (1), the owner: "Armor by hit can go on"): on by default on the action clock — on unless false; the classic
 * turn game's only when set (it stays frozen).
 */
export function zonesOn(config: Pick<GameState['config'], 'vehicleZones' | 'clock'> | undefined): boolean {
  return config?.vehicleZones === true || (config?.clock === 'action' && config.vehicleZones !== false);
}

/** A body's zone table, or null for a kind the zones do not cover. */
export function zoneTableOf(s: Pick<StructureState, 'type'>): ZoneTable | null {
  return VEHICLE_ZONES[s.type as ZonedVehicle] ?? null;
}

/** A world point in the hull's body frame (people: x across, y up, z forward, from its feet) — the frame its hit shape stands in (core/hits.ts movingShapes). */
export function hullLocal(state: Pick<GameState, 'planets' | 'config'>, s: Pick<StructureState, 'type' | 'surfaceDir' | 'drive' | 'planetIndex'>, point: Vec3): Local | null {
  return hullFrameOf(state, s)?.local(point) ?? null;
}

/** 69.29 step 4: the hull's frame — a world point in it (people), a world direction in it (unit-free). */
function hullFrameOf(state: Pick<GameState, 'planets' | 'config'>, s: Pick<StructureState, 'type' | 'surfaceDir' | 'drive' | 'planetIndex'>): { local: (p: Vec3) => Local; dir: (v: Vec3) => Local } | null {
  const planet = state.planets[s.planetIndex];
  if (!planet) return null;
  const ps = personSetOf(state), person = PERSON_BODY_HEIGHT * ps.k;
  const f = vehicleFrame(planet, s, person, { fineAmplitude: ps.fineAmplitude, fineWavelength: ps.fineWavelength, k: ps.k });
  return {
    local: p => { const rel = vecSub(p, f.foot); return [vecDot(rel, f.left) / person, vecDot(rel, f.up) / person, vecDot(rel, f.front) / person]; },
    dir: v => [vecDot(v, f.left), vecDot(v, f.up), vecDot(v, f.front)],
  };
}

/** The bound of a hull's measured boxes (people, its frame), or null with none. */
function hullBound(type: StructureState['type']): { lo: number[]; hi: number[] } | null {
  const def = HITBOXES[type as BodyKey];
  const lo = [Infinity, Infinity, Infinity], hi = [-Infinity, -Infinity, -Infinity];
  for (const part of def?.parts ?? []) {
    if (part.shape !== 'box') continue;
    for (let i = 0; i < 3; i++) { lo[i] = Math.min(lo[i], part.centre[i] - part.half[i]); hi[i] = Math.max(hi[i], part.centre[i] + part.half[i]); }
  }
  return Number.isFinite(lo[0]) ? { lo, hi } : null;
}

/**
 * 69.29 step 4: the cosine a flight `d` (its frame) meets the face of the hull's bound nearest `p` at — the face the point
 * lies most outward on, its outward normal against the flight (1: square on; a glance toward 0). 1 with no flight or bound.
 */
function faceCos(type: StructureState['type'], p: Local, d: Local): number {
  const b = hullBound(type), len = Math.sqrt(d[0] * d[0] + d[1] * d[1] + d[2] * d[2]);
  if (!b || len < 1e-9) return 1;
  let axis = 0, out = -Infinity;
  for (let i = 0; i < 3; i++) {
    const c = (b.lo[i] + b.hi[i]) / 2, h = Math.max(1e-9, (b.hi[i] - b.lo[i]) / 2), r = Math.abs(p[i] - c) / h;
    if (r > out) { out = r; axis = i; }
  }
  const c = (b.lo[axis] + b.hi[axis]) / 2, normal = p[axis] >= c ? 1 : -1;
  return Math.max(0, Math.min(1, -(d[axis] * normal) / len));
}

/** The zone of a point in a table's frame: the first region holding it, else the plain hull (69.29: on a turret, its own, at its yaw). */
export function zoneAt(table: ZoneTable, p: Local, turretYaw = 0): VehicleZone {
  const t = table.turret;
  if (t && p[1] >= t.fromY) {
    // (into the turret's frame: turned back by its yaw about its axis — the drawn turret's rotation.y, core/hits.ts movingShapes)
    const c = turretYaw ? dcos(turretYaw) : 1, sn = turretYaw ? dsin(turretYaw) : 0, dz = p[2] - t.pivotZ;
    const q: Local = [p[0] * c - dz * sn, p[1], p[0] * sn + dz * c + t.pivotZ];
    if (q[0] >= t.lo[0] - 1e-6 && q[0] <= t.hi[0] + 1e-6 && q[1] >= t.lo[1] - 1e-6 && q[1] <= t.hi[1] + 1e-6 && q[2] >= t.lo[2] - 1e-6 && q[2] <= t.hi[2] + 1e-6) {
      for (const r of t.regions) if (inRegion(r, q)) return r.zone;
      return 'hull';
    }
  }
  for (const r of table.regions) {
    if (p[0] >= r.min[0] && p[0] <= r.max[0] && p[1] >= r.min[1] && p[1] <= r.max[1] && p[2] >= r.min[2] && p[2] <= r.max[2]) return r.zone;
  }
  return 'hull';
}

/** The point of a hull's measured shape nearest `p` (its boxes' bound): where a burst outside it meets it. */
export function nearestOnHull(type: StructureState['type'], p: Local): Local {
  const def = HITBOXES[type as BodyKey];
  let lo = [Infinity, Infinity, Infinity], hi = [-Infinity, -Infinity, -Infinity];
  for (const part of def?.parts ?? []) {
    if (part.shape !== 'box') continue;
    for (let i = 0; i < 3; i++) { lo[i] = Math.min(lo[i], part.centre[i] - part.half[i]); hi[i] = Math.max(hi[i], part.centre[i] + part.half[i]); }
  }
  if (!Number.isFinite(lo[0])) { lo = [0, 0, 0]; hi = [0, 0, 0]; }
  return [Math.min(hi[0], Math.max(lo[0], p[0])), Math.min(hi[1], Math.max(lo[1], p[1])), Math.min(hi[2], Math.max(lo[2], p[2]))];
}

/**
 * The zone a hit on vehicle `s` at world `point` struck (a round's entry point; a burst's centre is first brought to the
 * hull's nearest point), its point in the hull's frame, and the zone's rating — null for a kind the zones do not cover.
 */
export function zoneOf(state: Pick<GameState, 'planets' | 'config'>, s: Pick<StructureState, 'type' | 'surfaceDir' | 'drive' | 'planetIndex'> & Partial<Pick<StructureState, 'turretYaw'>>, point: Vec3, burst = false, flight?: Vec3): { zone: VehicleZone; local: Local; rating: number; cos: number; effective: number } | null {
  const table = zoneTableOf(s);
  if (!table) return null;
  simWork.zoneLookups++;   // (the probes' count: nothing reads it)
  const fr = hullFrameOf(state, s);
  if (!fr) return null;
  const raw = fr.local(point);
  const local = burst ? nearestOnHull(s.type, raw) : raw;
  const zone = zoneAt(table, local, s.turretYaw ?? 0), rating = table.rating[zone];
  // 69.29 step 4: the angle it meets the face at — a round's flight, a burst's from where it went off to the hull (inside: square)
  const d: Local = flight ? fr.dir(flight) : [local[0] - raw[0], local[1] - raw[1], local[2] - raw[2]];
  const cos = faceCos(s.type, local, d);
  return { zone, local, rating, cos, effective: angledRating(table.cls, rating, cos) };
}

/** 69.29 step 4: whether the angle and the wear count for a class (the armoured and the heavy: angledRating's own set). */
export function angled(cls: VehicleClass): boolean { return ANGLED.has(cls); }

/**
 * 69.29 step 4 (DECISIONS 246): the share of a hit's blow a zoned hull takes — on the armoured and heavy classes its wear by
 * the margin `pen − effective` (ZONE_RULES.wear: through or not, cumulative); on the rest whole when it gets through, the
 * stopped share when not (§5.24).
 */
export function hullShare(cls: VehicleClass, pen: number, effective: number): number {
  if (!angled(cls)) return pen >= effective ? 1 : ZONE_RULES.stoppedBurst;
  const w = ZONE_RULES.wear;
  return Math.max(w.floor, Math.min(1, w.base + w.perMargin * (pen - effective)));
}

/**
 * 69.29 step 6 (DECISIONS 246; the owner: "for law the front should be able to tank a bunch of rounds, from the side 2 rounds
 * at least"): the share of a round's blow a face is expected to take — the hits' own rule (hullShare on its angled rating,
 * angledRating): the one the bots' aim ranks a hull's faces by (ai/armourAim.ts rocketFace). The LAW (7) square on a tank: its
 * tail ~0.9, its side 0.72, its front ~0.17 (and at any angle the floor, 0.1).
 */
export function expectedShare(cls: VehicleClass, pen: number, rating: number, cos: number): number {
  return hullShare(cls, pen, angledRating(cls, rating, cos));
}
/** 69.29 step 6 (DECISIONS 246: "hold only when the expected damage is negligible"): a face whose expected share is at or under this is not worth a round — a tank's front square to a LAW (~0.17), a side at 45° (~0.18). */
export const EXPECTED_HOLD = 0.2;

/** 69.29 step 4 (DECISIONS 245 (3)): a penetrating hit through these zones is a mobility kill (StructureState.disabled). */
export function mobilityZone(zone: VehicleZone): 'running' | 'engine' | null {
  return zone === 'running' || zone === 'engine' ? zone : null;
}

/** 69.29 step 4: what a penetrating hit at `local` (the hull's frame) through `zone` disables — the running gear on the side it went through (+1 its left, −1 its right), or the engine; null for neither. */
export function knockedOut(zone: VehicleZone, local: Local): NonNullable<StructureState['disabled']> | null {
  const part = mobilityZone(zone);
  return part === 'running' ? { running: local[0] >= 0 ? 1 : -1 } : part === 'engine' ? { engine: true } : null;
}

/** 69.29 (§5.25 (b); DECISIONS 244, "with the tank"): the cosine a face's rating is divided by at most — a glance past ~73° counts as 73°. */
export const ANGLE_FLOOR = 0.3;
/** The classes the angle counts for (§5.25 (b)'s lean: the armoured and the heavy; the soft and light square, their ratings under every penetration that matters). */
const ANGLED: ReadonlySet<VehicleClass> = new Set(['armoured', 'heavy']);
/**
 * 69.29 (§5.25 (b)): a zone's effective rating met at cosine `cos` (θ between the round's flight, or a burst's direction from
 * the hull, and the face's normal) — rating / max(cos θ, ANGLE_FLOOR) for the armoured and heavy classes, the rating itself for
 * the rest. The one rule: the hits' (lane F's step 4) and the bots' aim (ai/armourAim.ts) both read it.
 */
export function angledRating(cls: VehicleClass, rating: number, cos: number): number {
  return ANGLED.has(cls) ? rating / Math.max(cos, ANGLE_FLOOR) : rating;
}

const inRegion = (r: { min: readonly number[]; max: readonly number[] }, p: Local) => p[0] >= r.min[0] && p[0] <= r.max[0] && p[1] >= r.min[1] && p[1] <= r.max[1] && p[2] >= r.min[2] && p[2] <= r.max[2];

const dist3 = (a: Local, b: Local) => { const x = a[0] - b[0], y = a[1] - b[1], z = a[2] - b[2]; return Math.sqrt(x * x + y * y + z * z); };

/** The seat behind zone `zone` nearest `at` within the outright reach (a seat that sits behind it), or -1. */
export function seatBehind(table: ZoneTable, zone: VehicleZone, at: Local): number {
  let best = -1, bestD: number = ZONE_RULES.outrightPeople;
  table.seats.forEach((seat, k) => {
    if (!seat.zones.includes(zone)) return;
    const d = dist3(seat.at, at);
    if (d <= bestD) { best = k; bestD = d; }
  });
  return best;
}

/**
 * What a burst that met vehicle `s` in `zone` (penetrating or not) does to the occupant of seat `seat`: killed outright (a
 * penetrating burst through the zone the seat sits behind, within reach of where it came in), a share of the blow it would
 * take where it sits (the rest inside, by the zone's pass share), or spared (a burst the hull stopped: inside, nothing).
 */
export function burstOnSeat(table: ZoneTable, hit: { zone: VehicleZone; local: Local }, penetrates: boolean, seat: number): { kind: 'outright' } | { kind: 'share'; share: number } | { kind: 'spared' } {
  if (!penetrates) return { kind: 'spared' };
  if (seatBehind(table, hit.zone, hit.local) === seat) return { kind: 'outright' };
  return { kind: 'share', share: ZONE_RULES.passShare[hit.zone] };
}

/** A burst's zone context for core/damage.ts calculateDamage: the hit on each zoned vehicle it reaches and whether it gets through. */
export interface BurstZones { pen: number; at: Vec3; state: Pick<GameState, 'planets' | 'config'> }

/** 63.12 (C2): how much a vehicle's load widens its death burst — the launcher rounds aboard (in its tubes and its store; absent = full), by √, capped. */
export function loadWidening(s: Pick<StructureState, 'seats'>): number {
  let rounds = 0;
  for (const seat of s.seats ?? []) if (seat.launcher) rounds += (seat.belt ?? MOUNT_LAUNCHERS[seat.launcher].magazine) + (seat.spare ?? MOUNT_LAUNCHERS[seat.launcher].store);
  return Math.min(ZONE_RULES.load.cap, 1 + ZONE_RULES.load.perRoot * Math.sqrt(rounds));
}
